import {useRef, useState} from "react";
import {useMessageContext} from "../../contexts/MessageContext";
import {
  Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography
} from "@mui/material";
import {fullManualBrandingBanner} from "../../services/BrandingBannerService";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const BrandingBannerPlayground = ({companies, setLoading}) => {

  const [data, setData] = useState({});
  const inputFileRef = useRef(null);
  const [imageTemplate, setImageTemplate] = useState({});
  const {notificationHandler} = useMessageContext();
  const [selectedFont, setSelectedFont] = useState("Inter-Bold");

  const fonts = [
    "Inter-Bold",
    "Inter-Regular",
    "Helvetica-Bold",
    "Helvetica-Regular",
    "Lato-Bold",
    "Lato-Regular",
    "Montserrat-Bold",
    "Montserrat-Regular",
    "OpenSans-Bold",
    "OpenSans-Regular",
    "Poppins-Bold",
    "Poppins-Regular",
    "Roboto-Bold",
    "Roboto-Regular",
    "SourceSerif4-Bold",
    "SourceSerif4-Regular",
    "Ubuntu-Bold",
    "Ubuntu-Regular",
    "Manrope-Bold",
    "Manrope-Regular",
    "NotoSans-Bold",
    "NotoSans-Regular",
  ];

  const handleSubmit = () => {
    setLoading(true);

    let body = {
      ...data,
      base64Image: imageTemplate.imageBase64,
      companyId: data.company?.id,
      font: selectedFont
    };

    delete body.company;

    fullManualBrandingBanner(body)
        .then(() => setData({}))
        .then(() => setImageTemplate({}))
        .then(() => notificationHandler.success('Manual Branding Banner triggered'))
        .finally(() => setLoading(false));
  };

  const onFileChange = async (e) => {
    let body = await Promise.all([...e.target.files].map(async file => {
      return {
        name: file.name,
        imageBase64: await convertBase64(file)
      }
    }));
    setImageTemplate(body[0]);
  }

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file)
      fileReader.onload = () => {
        resolve(fileReader.result);
      }
      fileReader.onerror = (error) => {
        reject(error);
      }
    })
  }

  return (
    <Box>
      {/* Content Settings */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Content Settings
      </Typography>

      <TextField
          fullWidth
          required
          multiline
          rows={2}
          size="small"
          value={data.imageTitle || ""}
          margin="dense"
          id="imageTitle"
          name="imageTitle"
          label="Image Title"
          placeholder="Enter banner title..."
          onChange={e => setData(prv => ({ ...prv, imageTitle: e.target.value }))}
          type="text"
          variant="outlined"
          sx={{ mb: 2 }}
      />

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Company (Optional)</InputLabel>
        <Select
            label="Company (Optional)"
            value={data.company || ""}
            onChange={e => setData(prv => ({ ...prv, company: e.target.value }))}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {companies.map((company, i) => (
            <MenuItem key={i} value={company}>{company.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      {/* Image Upload */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Image Settings
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
        >
          Upload Image
          <input
              type="file"
              hidden
              ref={inputFileRef}
              onChange={onFileChange}
          />
        </Button>

        {imageTemplate.imageBase64 && (
          <Box
              component="img"
              sx={{ height: 80, width: 80, objectFit: "cover", borderRadius: 1, border: "1px solid #E7E9EE" }}
              src={imageTemplate.imageBase64}
          />
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Text Styling */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Text Styling
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="color"
            name="color"
            label="Color"
            type="text"
            variant="outlined"
            value={data.color || ""}
            onChange={e => setData(prv => ({ ...prv, color: e.target.value }))}
        />
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="fontSize"
            name="fontSize"
            label="Font Size"
            type="number"
            variant="outlined"
            value={data.fontSize || ""}
            onChange={e => setData(prv => ({ ...prv, fontSize: e.target.value }))}
        />
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Font</InputLabel>
          <Select
              label="Font"
              value={selectedFont}
              onChange={(e) => setSelectedFont(e.target.value)}
          >
            {fonts.map((font, i) => (
              <MenuItem key={i} value={font}>{font}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Position & Size */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
        Position & Size
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="x"
            name="x"
            label="X Position"
            type="number"
            variant="outlined"
            value={data.x || ""}
            onChange={e => setData(prv => ({ ...prv, x: e.target.value }))}
        />
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="y"
            name="y"
            label="Y Position"
            type="number"
            variant="outlined"
            value={data.y || ""}
            onChange={e => setData(prv => ({ ...prv, y: e.target.value }))}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="maxWidth"
            name="maxWidth"
            label="Max Width"
            type="number"
            variant="outlined"
            value={data.maxWidth || ""}
            onChange={e => setData(prv => ({ ...prv, maxWidth: e.target.value }))}
        />
        <TextField
            size="small"
            sx={{ flex: 1 }}
            id="maxHeight"
            name="maxHeight"
            label="Max Height"
            type="number"
            variant="outlined"
            value={data.maxHeight || ""}
            onChange={e => setData(prv => ({ ...prv, maxHeight: e.target.value }))}
        />
      </Box>

      {/* Submit Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
            variant="contained"
            size="large"
            startIcon={<PlayArrowIcon />}
            disabled={!data.imageTitle}
            onClick={handleSubmit}
        >
          Generate Banner
        </Button>
      </Box>
    </Box>
  )
};

export default BrandingBannerPlayground;
